import type { NavigationMenuElement, NavigationMenuLinkColors } from "../types";

export const NAV_MENU_DATA_ATTR = "data-pb-nav-menu";

/** Scope key for link-state CSS (`:hover`, `[aria-current="page"]`). */
export function navMenuDataAttr(
	elementId: string
): Record<typeof NAV_MENU_DATA_ATTR, string> {
	return { [NAV_MENU_DATA_ATTR]: elementId };
}

function cssEscapeAttrValue(value: string): string {
	return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function buildNavigationMenuLinkCss(
	elementId: string,
	colors: NavigationMenuLinkColors
): string {
	if (!elementId.trim()) {
		return "";
	}
	const sel = `[${NAV_MENU_DATA_ATTR}="${cssEscapeAttrValue(elementId)}"]`;
	const rules: string[] = [];
	const link = colors.linkColor?.trim();
	const hover = colors.linkHoverColor?.trim();
	const active = colors.linkActiveColor?.trim();
	if (link) {
		rules.push(`${sel} a { color: ${link}; }`);
	}
	if (hover) {
		rules.push(`${sel} a:hover { color: ${hover}; }`);
	}
	if (active) {
		rules.push(`${sel} a[aria-current="page"] { color: ${active}; }`);
	}
	return rules.join("\n");
}

/** Explicit link color, else inherited nav `styles.color`. */
/** Strip query/hash and normalize slashes for nav active-state comparison. */
export function normalizeNavigationPath(
	raw: string | null | undefined
): string {
	if (typeof raw !== "string" || !raw.trim()) {
		return "/";
	}
	let path = raw.trim();
	if (/^https?:\/\//i.test(path)) {
		try {
			path = new URL(path).pathname;
		} catch {
			return "/";
		}
	}
	const q = path.indexOf("?");
	const h = path.indexOf("#");
	let end = path.length;
	if (q >= 0) {
		end = Math.min(end, q);
	}
	if (h >= 0) {
		end = Math.min(end, h);
	}
	path = path.slice(0, end);
	if (!path.startsWith("/")) {
		path = `/${path}`;
	}
	if (path.length > 1 && path.endsWith("/")) {
		path = path.slice(0, -1);
	}
	return path || "/";
}

/** True when `href` refers to the current public pathname (for `aria-current="page"`). */
export function isNavigationHrefCurrent(
	href: string | null | undefined,
	currentPathname: string | null | undefined
): boolean {
	const target = normalizeNavigationPath(href);
	if (!href?.trim() || target === "#" || href.trim().startsWith("#")) {
		return false;
	}
	const current = normalizeNavigationPath(currentPathname);
	if (target === "/") {
		return current === "/";
	}
	return current === target || current.startsWith(`${target}/`);
}

export function resolveNavigationMenuLinkColor(
	nav: Pick<NavigationMenuElement, "linkColor" | "styles">
): string | undefined {
	const explicit = nav.linkColor?.trim();
	if (explicit) {
		return explicit;
	}
	const inherited = nav.styles?.color?.trim();
	return inherited || undefined;
}

/** Map nav orientation ↔ CSS flex-direction (desktop prop + responsiveOverrides). */
export function flexDirectionFromNavOrientation(
	orientation: "horizontal" | "vertical"
): "row" | "column" {
	return orientation === "vertical" ? "column" : "row";
}

export function navOrientationFromFlexDirection(
	flexDirection: string | undefined,
	fallback: "horizontal" | "vertical" = "horizontal"
): "horizontal" | "vertical" {
	const dir = flexDirection?.trim().toLowerCase() ?? "";
	if (dir === "column" || dir === "column-reverse") {
		return "vertical";
	}
	if (dir === "row" || dir === "row-reverse") {
		return "horizontal";
	}
	return fallback;
}

/** Resolve preview/publish flex direction from styles + orientation fallback. */
export function resolveNavMenuFlexDirection(
	flexDirection: string | undefined,
	orientation: "horizontal" | "vertical" | undefined
): "row" | "column" {
	const fromStyle = flexDirection?.trim().toLowerCase() ?? "";
	if (fromStyle === "column" || fromStyle === "column-reverse") {
		return "column";
	}
	if (fromStyle === "row" || fromStyle === "row-reverse") {
		return "row";
	}
	return flexDirectionFromNavOrientation(
		orientation === "vertical" ? "vertical" : "horizontal"
	);
}
